import React, { useState, useEffect } from 'react';
import { T, font, mono, COMPANY } from '../utils/theme';
import * as api from '../utils/api';
import { setUnsaved } from '../utils/unsaved';
import { Card, Sect, Btn, Inp, Modal, SearchBar, SBadge, ViewToggle, IcCheck, IcEdit, IcTrash, IcPrint, IcMsg, IcPlus, useIsMobile, matchesSearch } from './UI';
import { money } from '../utils/money';

export default function SupplierQuotes({ lang, supplier, products, conversations, reload, Lk }) {
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState('table');
  const [localPrices, setLocalPrices] = useState({});
  const [savedFb, setSavedFb] = useState({});
  const [saveAllFb, setSaveAllFb] = useState(null);
  const [editQId, setEditQId] = useState(null);
  const [editQP, setEditQP] = useState('');
  // "Ask / tell us" on one row: a note, and "there is another version of this item".
  const [askP, setAskP] = useState(null);
  const [askFb, setAskFb] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [varForm, setVarForm] = useState({ label: '', price: '' });
  const [editingVar, setEditingVar] = useState(null);
  const [flashVar, setFlashVar] = useState(null);

  const canSee = p => !p.restrict_supplier || p.restrict_supplier === supplier.id;

  // To quote: no quote submitted yet (neither available nor unavailable)
  const toQuote = products.filter(p => p.status === 'quoting' && canSee(p) && !p.quotes?.[supplier.id]);

  // Already quoted: available quotes + unavailable (N/A) quotes - both editable
  const alreadyQuoted = products.filter(p => {
    const q = p.quotes?.[supplier.id];
    return canSee(p) && q && p.status === 'quoting' && p.assigned_supplier !== supplier.id;
  });

  const filt = (p) => !search || matchesSearch(search, p.name) || matchesSearch(search, p.sku || '');
  const filtTQ = toQuote.filter(filt);
  const filtQd = alreadyQuoted.filter(filt);

  const submitQuote = async (pid) => {
    const price = localPrices[pid];
    if (!price || parseFloat(price) <= 0) return;
    await api.submitQuote(pid, supplier.id, parseFloat(price), true);
    setSavedFb(prev => ({ ...prev, [pid]: true }));
    setTimeout(() => setSavedFb(prev => { const n = { ...prev }; delete n[pid]; return n; }), 2500);
    setLocalPrices(prev => { const n = { ...prev }; delete n[pid]; return n; });
    await reload();
  };

  const markUnavail = async (pid) => {
    await api.submitQuote(pid, supplier.id, 0, false);
    setLocalPrices(prev => { const n = { ...prev }; delete n[pid]; return n; });
    await reload();
  };

  // The product as it stands now, not as it was when the modal opened: adding a version changes it
  // (it gains a group), and the list below has to follow.
  const askProd = askP ? (products.find(x => x.id === askP.id) || askP) : null;
  // Everything this supplier suggested for this item, accepted ones included: dropping them from the
  // list the moment we accept would look like they had been thrown away.
  const mySuggestions = askProd
    ? products.filter(x => x.proposed_by === supplier.id && x.variant_parent_id === askProd.id)
    : [];
  // A row this supplier has already said something about - a note, or a version they suggested -
  // is marked, so they can see at a glance which ones they have dealt with.
  const noteCount = (pid) => (conversations || []).filter(c => c.product_id === pid).length
    + products.filter(x => x.proposed_by === supplier.id && x.variant_parent_id === pid).length;

  // Prices typed into the list but not saved yet. App asks before reloading on a tab click, so
  // clicking Quotes half way through entering a column does not throw the column away.
  useEffect(() => {
    setUnsaved(Object.values(localPrices).some(v => String(v == null ? '' : v).trim() !== ''));
    return () => setUnsaved(false);
  }, [localPrices]);

  const openAsk = async (p) => {
    setAskP(p); setAskFb(null); setNoteText(''); setVarForm({ label: '', price: '' }); setEditingVar(null);
  };
  const closeAsk = () => { setAskP(null); setAskFb(null); setEditingVar(null); };
  const resetVarForm = () => { setVarForm({ label: '', price: '' }); setEditingVar(null); };
  const flash = (id) => { setFlashVar(id); setTimeout(() => setFlashVar(null), 2600); };

  const sendNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    await api.addConversation({ product_id: askProd.id, text });
    setNoteText('');
    setAskFb({ ok: true, msg: lang === 'zh' ? '备注已发送。' : 'Note sent.' });
    await reload();
  };

  const submitVariant = async () => {
    const label = varForm.label.trim();
    const price = String(varForm.price).trim();
    if (!label) { setAskFb({ ok: false, msg: lang === 'zh' ? '请填写版本。' : 'Describe the version first.' }); return; }
    if (!price || !(parseFloat(price) > 0)) { setAskFb({ ok: false, msg: lang === 'zh' ? '请填写您的价格。' : 'Please enter your price for this version.' }); return; }
    try {
      const r = editingVar
        ? await api.editVariant(editingVar, { label, price })
        : await api.proposeVariant(askProd.id, { label, price });
      const wasEdit = !!editingVar;
      resetVarForm();
      setAskFb({ ok: true, msg: wasEdit ? (lang === 'zh' ? '已更新' : 'Updated') : (lang === 'zh' ? '已加入列表' : 'Added to list') });
      flash(r.id);
      await reload();
    } catch (e) {
      const m = String(e.message || '');
      setAskFb({ ok: false, msg: m === 'already_listed'
        ? (lang === 'zh' ? '该版本已在列表中，请直接为它报价。' : 'That version is already in the list — quote it in your own list instead.')
        : m });
    }
  };

  const startEditVar = (v) => {
    setEditingVar(v.id);
    setVarForm({ label: v.variant_label || v.name, price: String(v.quotes?.[supplier.id]?.price != null ? v.quotes[supplier.id].price : '') });
    setAskFb(null);
  };

  const removeVar = async (v) => {
    try {
      await api.withdrawVariant(v.id);
      if (editingVar === v.id) resetVarForm();
      setAskFb({ ok: true, msg: lang === 'zh' ? '已移除' : 'Removed' });
      await reload();
    } catch (e) { setAskFb({ ok: false, msg: String(e.message || '') }); }
  };

  const saveAll = async () => {
    let ct = 0;
    for (const p of filtTQ) {
      const price = localPrices[p.id];
      if (price && parseFloat(price) > 0) {
        await api.submitQuote(p.id, supplier.id, parseFloat(price), true);
        ct++;
        setSavedFb(prev => ({ ...prev, [p.id]: true }));
        setTimeout(() => setSavedFb(prev => { const n = { ...prev }; delete n[p.id]; return n; }), 2500);
      }
    }
    setLocalPrices({});
    const msg = ct > 0 ? `✓ ${lang === 'zh' ? `已保存 ${ct} 个报价` : `Saved ${ct} quote${ct > 1 ? 's' : ''}`}` : (lang === 'zh' ? '未输入价格' : 'No prices entered');
    setSaveAllFb(msg);
    setTimeout(() => setSaveAllFb(null), 3000);
    await reload();
  };

  const editQuotedPrice = async (pid) => {
    if (!editQP || parseFloat(editQP) <= 0) return;
    await api.editQuote(pid, supplier.id, parseFloat(editQP), false);
    // Also mark as available in case it was N/A before
    await api.submitQuote(pid, supplier.id, parseFloat(editQP), true);
    setEditQId(null);
    setEditQP('');
    setSavedFb(prev => ({ ...prev, [pid]: true }));
    setTimeout(() => setSavedFb(prev => { const n = { ...prev }; delete n[pid]; return n; }), 2500);
    await reload();
  };

  const doPrintToQuote = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Products to Quote</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#333}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#222;color:#fff;padding:10px 14px;text-align:left;font-size:13px}td{padding:8px 14px;border-bottom:1px solid #ddd;font-size:13px}.header{display:flex;justify-content:space-between;margin-bottom:30px}.info{text-align:right;font-size:12px;line-height:1.6}</style></head><body>`);
    w.document.write(`<div class="header"><div><strong>${COMPANY.name}</strong></div><div class="info"><strong>${COMPANY.legal}</strong><br>${COMPANY.addr}<br>${COMPANY.city}<br>${COMPANY.phone}</div></div>`);
    w.document.write(`<div style="font-size:11px;color:#888;margin-bottom:4px">${new Date().toLocaleString()}</div>`);
    w.document.write(`<div style="font-size:18px;font-weight:bold;margin-bottom:10px">PRODUCTS TO QUOTE — ${supplier.name}</div>`);
    w.document.write(`<table><thead><tr><th>Product</th><th>SKU</th><th>Price</th></tr></thead><tbody>`);
    filtTQ.forEach(p => { w.document.write(`<tr><td>${p.name}</td><td>${p.sku || '—'}</td><td></td></tr>`); });
    w.document.write(`</tbody></table></body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('quotesTab')}</h2>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', position: 'sticky', top: 0, zIndex: 20, background: T.bg, paddingTop: 4, paddingBottom: 8 }}>
        <SearchBar value={search} onChange={setSearch} placeholder={Lk('searchProduct')} style={{ flex: 1 }} />
        <ViewToggle mode={viewMode} setMode={setViewMode} lang={lang} />
      </div>

      {/* Products to Quote */}
      <Sect title={`${Lk('productsToQuote')} (${filtTQ.length})`} right={filtTQ.length > 0 ? <Btn variant="ghostSm" onClick={doPrintToQuote}>{IcPrint} {Lk('print')}</Btn> : null}>
        {filtTQ.length === 0 ? (
          <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic' }}>{lang === 'zh' ? '暂无待报价产品。' : 'No products awaiting your quote.'}</div>
        ) : (!isMobile && viewMode === 'table') ? (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
              <thead><tr>{[Lk('productName'), Lk('sku'), Lk('price'), Lk('action')].map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {filtTQ.map(p => {
                  const js = savedFb[p.id];
                  const pv = localPrices[p.id] !== undefined ? localPrices[p.id] : '';
                  return (
                    <tr key={p.id} style={{ background: js ? T.successBg : 'transparent', transition: 'background 0.3s' }}>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.text }}>
                        {p.name}
                        {p.pinned_comment && <div style={{ fontSize: 11, color: T.orange, marginTop: 2 }}>📌 {p.pinned_comment}</div>}
                        {p.variant_state === 'provisional' && p.restrict_supplier === supplier.id && (
                          <div style={{ fontSize: 11, color: T.warn, marginTop: 2 }}>{lang === 'zh' ? '您建议的版本 · 等待审核' : 'Version you suggested · awaiting review'}</div>
                        )}
                        {p.variant_state === 'provisional' && !p.restrict_supplier && (
                          <div style={{ fontSize: 11, color: T.accent, marginTop: 2 }}>{lang === 'zh' ? '新版本' : 'New version'}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.sku}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                        {js ? <span style={{ fontFamily: mono, fontWeight: 700, color: T.successBr }}>{money(parseFloat(pv || 0))} ✓</span> : (
                          <input type="text" inputMode="decimal" placeholder={Lk('enterPrice')} value={pv}
                            onChange={e => setLocalPrices({ ...localPrices, [p.id]: e.target.value })}
                            style={{ fontFamily: mono, fontSize: 13, padding: '6px 10px', width: 100, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {!js && <Btn variant="small" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => submitQuote(p.id)}>{lang === 'zh' ? '报价' : 'Quote'}</Btn>}
                          {!js && <Btn variant="dangerSm" onClick={() => markUnavail(p.id)}>N/A</Btn>}
                          <Btn variant="ghostSm" onClick={() => openAsk(p)} title={lang === 'zh' ? '备注 / 其他版本' : 'Note / another version'}
                            style={noteCount(p.id) ? { color: T.accent, borderColor: T.accent, background: T.surfaceAlt, fontWeight: 800 } : undefined}>
                            {IcMsg}{noteCount(p.id) ? ' ' + noteCount(p.id) : ''}</Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
              <Btn onClick={saveAll}>{IcCheck} {Lk('saveAllQuotes')}</Btn>
              {saveAllFb && <span style={{ fontSize: 13, fontWeight: 700, color: saveAllFb.startsWith('✓') ? T.success : T.dim }}>{saveAllFb}</span>}
            </div>
          </>
        ) : (
          <>
            {filtTQ.map(p => {
              const js = savedFb[p.id];
              const pv = localPrices[p.id] !== undefined ? localPrices[p.id] : '';
              return (
                <Card key={p.id} style={{ marginBottom: 6, background: js ? T.successBg : T.surface, borderColor: js ? T.success + '40' : T.border }}>
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{p.name}</span>
                      <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 8 }}>{p.sku}</span>
                      {p.pinned_comment && <div style={{ fontSize: 11, color: T.orange, marginTop: 2 }}>📌 {p.pinned_comment}</div>}
                    </div>
                    {js ? <span style={{ fontFamily: mono, fontWeight: 700, color: T.successBr }}>{money(parseFloat(pv || 0))} ✓</span> : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="text" inputMode="decimal" placeholder={Lk('enterPrice')} value={pv}
                          onChange={e => setLocalPrices({ ...localPrices, [p.id]: e.target.value })}
                          style={{ fontFamily: mono, fontSize: 14, padding: '10px 12px', flex: isMobile ? 1 : undefined, width: isMobile ? undefined : 100, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                        <Btn variant="small" onClick={() => submitQuote(p.id)} style={isMobile ? { padding: '10px 16px' } : {}}>{lang === 'zh' ? '报价' : 'Quote'}</Btn>
                        <Btn variant="dangerSm" onClick={() => markUnavail(p.id)} style={isMobile ? { padding: '10px 12px' } : {}}>N/A</Btn>
                        <Btn variant="ghostSm" onClick={() => openAsk(p)}
                          style={{ ...(isMobile ? { padding: '10px 12px' } : {}), ...(noteCount(p.id) ? { color: T.accent, borderColor: T.accent, background: T.surfaceAlt, fontWeight: 800 } : {}) }}>
                          {IcMsg}{noteCount(p.id) ? ' ' + noteCount(p.id) : ''}</Btn>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
              <Btn onClick={saveAll} style={isMobile ? { flex: 1, justifyContent: 'center', padding: '14px' } : {}}>{IcCheck} {Lk('saveAllQuotes')}</Btn>
              {saveAllFb && <span style={{ fontSize: 13, fontWeight: 700, color: saveAllFb.startsWith('✓') ? T.success : T.dim }}>{saveAllFb}</span>}
            </div>
          </>
        )}
      </Sect>

      {/* Products Quoted (includes N/A items with edit) */}
      {filtQd.length > 0 && (
        <Sect title={`${Lk('productsQuoted')} (${filtQd.length})`}>
          {viewMode === 'table' ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
              <thead><tr>{[Lk('productName'), Lk('sku'), Lk('price'), Lk('action')].map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {filtQd.map(p => {
                  const q = p.quotes?.[supplier.id];
                  const js = savedFb[p.id];
                  const isEditing = editQId === p.id;
                  const isNA = q && !q.available;

                  return (
                    <tr key={p.id} style={{ background: js ? T.successBg : 'transparent', transition: 'background 0.3s' }}>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.text }}>
                        {p.name}
                        {p.variant_state === 'provisional' && p.restrict_supplier === supplier.id && (
                          <div style={{ fontSize: 11, color: T.warn, marginTop: 2 }}>{lang === 'zh' ? '您建议的版本 · 等待审核' : 'Version you suggested · awaiting review'}</div>
                        )}
                        {p.variant_state === 'provisional' && !p.restrict_supplier && (
                          <div style={{ fontSize: 11, color: T.accent, marginTop: 2 }}>{lang === 'zh' ? '新版本' : 'New version'}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.sku}</td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                        {js ? <span style={{ fontFamily: mono, fontWeight: 700, color: T.successBr }}>{money(parseFloat(editQP || q?.price || 0))} ✓</span>
                          : isEditing ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input type="text" inputMode="decimal" value={editQP} onChange={e => setEditQP(e.target.value)} autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') editQuotedPrice(p.id); if (e.key === 'Escape') setEditQId(null); }}
                                style={{ fontFamily: mono, fontSize: 13, padding: '4px 8px', width: 80, background: T.bg, border: `1px solid ${T.accent}`, borderRadius: 4, color: T.text, outline: 'none' }} />
                              <Btn variant="small" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => editQuotedPrice(p.id)}>{Lk('save')}</Btn>
                              <Btn variant="ghost" style={{ fontSize: 11, padding: '4px 6px' }} onClick={() => setEditQId(null)}>✕</Btn>
                            </div>
                          ) : isNA ? (
                            <SBadge label={lang === 'zh' ? '缺货' : 'Unavailable'} color={T.danger} bg={T.dangerBg} />
                          ) : (
                            <span style={{ fontFamily: mono, fontWeight: 700, color: T.success }}>{money(q?.price)}</span>
                          )}
                        {q?.pendingReview && <span style={{ marginLeft: 6 }}><SBadge label={Lk('pendingReview')} color={T.warn} bg={T.warnBg} /></span>}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                        {!js && !isEditing && (
                          <Btn variant="ghostSm" onClick={() => { setEditQId(p.id); setEditQP(isNA ? '' : String(q?.price || '')); }}>{IcEdit} {Lk('edit')}</Btn>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            filtQd.map(p => {
              const q = p.quotes?.[supplier.id];
              const js = savedFb[p.id];
              const isNA = q && !q.available;
              return (
                <Card key={p.id} style={{ marginBottom: 6, background: js ? T.successBg : T.surface }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{p.name}</span>
                      <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 8 }}>{p.sku}</span>
                    </div>
                    {isNA ? <SBadge label="N/A" color={T.danger} bg={T.dangerBg} /> : <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 700, color: T.success }}>{money(q?.price)}</span>}
                    <Btn variant="ghostSm" onClick={() => { setEditQId(p.id); setEditQP(isNA ? '' : String(q?.price || '')); }}>{IcEdit}</Btn>
                  </div>
                </Card>
              );
            })
          )}
        </Sect>
      )}
      {/* Suggesting a version comes first: it is worth more to both sides than a note, because a note
          only reaches us while a version becomes something every supplier can price. */}
      <Modal open={!!askProd} onClose={closeAsk}>
        {askProd && (
          <div>
            <style>{`@keyframes mcFlashRow {
              0%, 100% { background: ${T.surface}; box-shadow: none; }
              20%, 60% { background: ${T.warnBg}; box-shadow: 0 0 0 3px ${T.warn}66; }
            }`}</style>
            <div style={{ fontSize: 16, fontWeight: 900, color: T.text, marginBottom: 2 }}>{askProd.name}</div>
            <div style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginBottom: 18 }}>{askProd.sku}</div>

            <div style={{ fontSize: 11, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 6 }}>{lang === 'zh' ? '还有其他版本？' : 'Another version of this item?'}</div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 12, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '如果该产品有我们未列出的版本（复制 / 原拆 / 原装新 / 颜色 / 美版），请填写版本名称和您的价格。'
                : 'If this item comes in a version we do not list (copy, original pull, original new, a colour, a region), add it here with your price.'}
            </div>


            <Inp label={lang === 'zh' ? '版本' : 'Version'} value={varForm.label}
              placeholder={lang === 'zh' ? '例如：复制 / 原拆 / 灰色 / 美版' : 'e.g. copy / original pull / grey / US version'}
              onChange={e => setVarForm({ ...varForm, label: e.target.value })} />
            <div style={{ height: 10 }} />
            <Inp label={lang === 'zh' ? '您的价格 *' : 'Your price *'} value={varForm.price}
              placeholder="0.00" inputMode="decimal" onChange={e => setVarForm({ ...varForm, price: e.target.value })} style={{ maxWidth: 150 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <Btn onClick={submitVariant}>{editingVar ? <>{IcCheck} {lang === 'zh' ? '保存修改' : 'Save changes'}</> : <>{IcPlus} {lang === 'zh' ? '加入列表' : 'Add this version'}</>}</Btn>
              {editingVar
                ? <Btn variant="ghost" onClick={resetVarForm}>{Lk('cancel')}</Btn>
                : <Btn variant="ghost" onClick={closeAsk}>{Lk('cancel')}</Btn>}
              {askFb && <span style={{ fontSize: 13, fontWeight: 700, color: askFb.ok ? T.success : T.danger }}>{askFb.msg}</span>}
            </div>

            {mySuggestions.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>{lang === 'zh' ? '您提交的版本列表' : 'Your suggested version list'}</div>
                {mySuggestions.map(v => {
                  const q = v.quotes?.[supplier.id];
                  const pending = !!v.restrict_supplier;
                  return (
                    <div key={v.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${pending ? T.warn : T.success}`,
                      borderRadius: 8, padding: '8px 12px', marginBottom: 6,
                      animation: flashVar === v.id ? 'mcFlashRow 0.7s ease-in-out 3' : undefined,
                    }}>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{v.variant_label || v.name}</div>
                        <div style={{ fontSize: 11, color: T.dim, marginTop: 1 }}>
                          {pending ? (lang === 'zh' ? '等待审核' : 'Waiting for review') : (lang === 'zh' ? '已开放报价' : 'Open for quoting')}
                        </div>
                      </div>
                      {q?.price != null && <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: T.success }}>{money(q.price)}</span>}
                      {pending ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Btn variant="ghostSm" title={lang === 'zh' ? '修改' : 'Edit this version'} onClick={() => startEditVar(v)}>{IcEdit}</Btn>
                          <Btn variant="dangerSm" title={lang === 'zh' ? '移除' : 'Remove this version'} onClick={() => removeVar(v)}>{IcTrash}</Btn>
                        </div>
                      ) : <SBadge label={lang === 'zh' ? '已确认' : 'Accepted'} color={T.success} bg={T.successBg} />}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ height: 1, background: T.border, margin: '20px 0 16px' }} />

            <div style={{ fontSize: 11, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 6 }}>{lang === 'zh' ? '备注' : 'Notes'}</div>
            {(conversations || []).filter(c => c.product_id === askProd.id).length === 0 ? (
              <div style={{ fontSize: 12, color: T.dim, fontStyle: 'italic', marginBottom: 8 }}>{lang === 'zh' ? '暂无备注。' : 'No notes yet.'}</div>
            ) : (conversations || []).filter(c => c.product_id === askProd.id).map(c => (
              <div key={c.id} style={{ background: c.author_type === 'admin' ? T.surfaceAlt : T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', marginBottom: 5 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: c.author_type === 'admin' ? T.accent : T.muted, textTransform: 'uppercase' }}>{c.author}</div>
                <div style={{ fontSize: 13, color: T.text, whiteSpace: 'pre-wrap' }}>{c.text}</div>
              </div>
            ))}
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2}
              placeholder={lang === 'zh' ? '例如：最少订购 50 个 / 交货 3 天' : 'e.g. minimum order 50 pcs / 3 day lead time'}
              style={{ width: '100%', boxSizing: 'border-box', fontFamily: font, fontSize: 13, padding: '8px 10px', background: T.bg, border: '1px solid ' + T.border, borderRadius: 6, color: T.text, outline: 'none', resize: 'vertical' }} />
            <Btn variant="small" onClick={sendNote} style={{ marginTop: 6 }}>{lang === 'zh' ? '发送备注' : 'Send note'}</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
